import React, {useEffect, useState} from 'react';
import {
    Alert,
    Box,
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    InputAdornment,
    TextField,
    Typography
} from '@mui/material';
import {Add, HubOutlined, Search, SearchOffOutlined} from '@mui/icons-material';
import {alpha} from '@mui/material/styles';
import {sharedTrafficSourceService} from '../../service/sharedTrafficSourceService';
import Spinner from '../spinner/Spinner';
import {SettingsDialogDescription} from '../settings/SettingsDialog';
import {getSettingsColors, SettingsEmptyState} from '../settings/SettingsDesign';

export const TrafficSourceTemplates = ({handleClose, handleSelectedTemplate}) => {
    const [loading, setLoading] = useState(true);
    const [requestError, setRequestError] = useState('');
    const [templates, setTemplates] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const filteredTemplates = templates.filter(item => item?.name?.toLowerCase().includes(searchQuery.trim().toLowerCase()));

    useEffect(() => {
        let active = true;
        sharedTrafficSourceService.read().then(response => {
            if (active) setTemplates(response?.sharedTrafficSources || []);
        }).catch(error => {
            if (active) setRequestError(error?.response?.data?.error || 'Unable to load traffic sources. You can still create a custom source.');
        }).finally(() => {
            if (active) setLoading(false);
        });
        return () => {
            active = false;
        };
    }, []);

    return <>
        <DialogTitle id="traffic-source-form-title">Choose your traffic source
            <SettingsDialogDescription>Start with a traffic source template or create your own
                configuration.</SettingsDialogDescription>
        </DialogTitle>
        <Box sx={{flexShrink: 0, px: {xs: 2, sm: 3}, pt: 1.5, pb: 1.5}}>
            <TextField
                className="traffic-source-search"
                variant="outlined"
                size="small"
                margin="dense"
                name="traffic-source"
                label="Search traffic sources"
                placeholder="Search by traffic source name"
                value={searchQuery}
                onChange={event => setSearchQuery(event.target.value)}
                fullWidth
                slotProps={{
                    inputLabel: {shrink: true},
                    input: {
                        startAdornment: <InputAdornment position="start"><Search fontSize="small"/></InputAdornment>
                    }
                }}
            />
        </Box>
        <DialogContent sx={{minHeight: {xs: 300, sm: 380}}}>
            {requestError && <Alert severity="error" sx={{mb: 2}}>{requestError}</Alert>}
            {loading ?
                <Box role="status"
                     sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, py: 6}}>
                    <Spinner size={24}/>
                    <Typography sx={{fontSize: 13, color: 'text.secondary'}}>Loading traffic sources…</Typography>
                </Box> : filteredTemplates.length ?
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: 'repeat(2, minmax(0, 1fr))',
                                sm: 'repeat(4, minmax(0, 1fr))'
                            },
                            gap: 1.25,
                            mt: 1
                        }}
                    >
                        {filteredTemplates.map(item => <Button key={item.id || item.name} color="inherit"
                                                               onClick={() => handleSelectedTemplate(item)}
                                                               sx={{
                                                                   minWidth: 0, p: 1.5, gap: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', borderRadius: '12px',
                                                                   bgcolor: theme => getSettingsColors(theme).inset, border: '1px solid', borderColor: 'divider',
                                                                   '&:hover': {borderColor: 'primary.main', bgcolor: theme => alpha(theme.palette.primary.main, 0.08)},
                                                                   '&.Mui-focusVisible': {outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2}
                                                               }}>
                            <Box
                                sx={{
                                    width: '100%', height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    bgcolor: theme => /white/i.test(item.logoUrl || '') ? theme.palette.text.darkBlue : alpha(theme.palette.common.white, 1), color: 'text.darkBlue', borderRadius: '8px', p: 1
                                }}
                            >
                                {item.logoUrl ? <Box component="img" src={item.logoUrl} alt=""
                                                     sx={{maxWidth: '100%', maxHeight: 28, objectFit: 'contain'}}/> :
                                    <HubOutlined fontSize="small"/>}
                            </Box>
                            <Typography
                                component="span"
                                sx={{fontSize: 12, fontWeight: 500, textAlign: 'center', overflowWrap: 'anywhere'}}>
                                {item.name}
                            </Typography>
                        </Button>)}
                    </Box> :
                    <SettingsEmptyState
                        icon={SearchOffOutlined}
                        title="No traffic sources found"
                        description="Try another name or create a custom source."
                    />
            }
        </DialogContent>
        <DialogActions>
            <Button color="inherit" onClick={handleClose}>Cancel</Button>
            <Button variant="contained" startIcon={<Add/>} onClick={() => handleSelectedTemplate(null)}>Create
                custom</Button>
        </DialogActions>
    </>;
};
